import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import axios from '../../../interceptors/axios';
import { MediaFolderCard, MediaFileCard, ThumbImg, isVideo } from '../media/MediaCard';
import { glassSx } from '../../../theme/sx';

export interface BrowserFile {
  id: number;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  duration_ms: number | null;
  default_scale_mode?: string;
  width?: number | null;
  height?: number | null;
  video_codec?: string | null;
}
interface BrowserFolder { id: number; name: string; parent_id?: number | null; file_count?: number; }

const fmtDuration = (ms: number) => {
  const totalS = ms / 1000;
  if (totalS < 60) return `${Math.floor(totalS)}s`;
  const m = Math.floor(totalS / 60);
  const s = Math.floor(totalS % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};


const IcoSearch    = () => <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const IcoBack      = () => <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>;
const IcoChevRight = () => <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>;
const IcoGrid      = () => <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>;
const IcoList      = () => <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3" cy="6" r="1.5" fill="currentColor" stroke="none"/><circle cx="3" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="3" cy="18" r="1.5" fill="currentColor" stroke="none"/></svg>;

// ── Props ──────────────────────────────────────────────────────────────────────
export interface MediaBrowserPanelProps {
  onFileDoubleClick: (file: BrowserFile) => void;
  onFilesAdd?: (files: BrowserFile[]) => void;
  /** Called with [file] normally, or all selected files when dragging a selected item with multi-select active */
  onFileDragStart?: (e: React.DragEvent, files: BrowserFile[]) => void;
  onDragEnd?: () => void;
  showDragHint?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────
const MediaBrowserPanel: React.FC<MediaBrowserPanelProps> = ({
  onFileDoubleClick,
  onFilesAdd,
  onFileDragStart,
  onDragEnd,
  showDragHint = false,
}) => {
  const { t } = useTranslation();
  const auth = useSelector((s: RootState) => s.auth.value);

  const [folders, setFolders] = useState<BrowserFolder[]>([]);
  const [files, setFiles] = useState<BrowserFile[]>([]);
  const [folderStack, setFolderStack] = useState<{ id: number | null; name: string }[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(
    () => (localStorage.getItem('pv_playlist_view') as 'grid' | 'list') || 'list'
  );

  // ── Multi-select ─────────────────────────────────────────────────────────────
  const [selectedFileIds, setSelectedFileIds] = useState<Set<number>>(new Set());
  const lastClickedIdxRef = useRef<number | null>(null);

  const currentFolderId = folderStack.length > 0 ? folderStack[folderStack.length - 1].id : null;

  useEffect(() => {
    setFolderStack([{ id: null, name: t('media.allFiles') }]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMedia = useCallback(async (folderId: number | null) => {
    setLoading(true);
    try {
      const [fRes, filesRes] = await Promise.all([
        axios.get<BrowserFolder[]>('/media/folders', { params: folderId !== null ? { parent_id: folderId } : {} }),
        axios.get<BrowserFile[]>('/media/files', { params: { folder_id: folderId !== null ? folderId : 'root' } }),
      ]);
      // Backend returns all folders for the company — filter by current parent
      setFolders(fRes.data.filter(f =>
        folderId === null ? (f.parent_id === null || f.parent_id === undefined) : f.parent_id === folderId
      ));
      setFiles(filesRes.data);
    } catch { /* interceptor */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (auth && folderStack.length > 0) loadMedia(currentFolderId);
  }, [auth, currentFolderId, loadMedia, folderStack.length]);

  const openFolder = (folder: BrowserFolder) => {
    setFolderStack(prev => [...prev, { id: folder.id, name: folder.name }]);
    setSelectedFileIds(new Set());
    lastClickedIdxRef.current = null;
  };
  const navToStackIndex = (idx: number) => {
    setFolderStack(prev => prev.slice(0, idx + 1));
    setSelectedFileIds(new Set());
    lastClickedIdxRef.current = null;
  };

  // Click handler for a file in the list (supports Ctrl/Cmd+click and Shift+click)
  const handleFileClick = (e: React.MouseEvent, file: BrowserFile, idx: number) => {
    if (e.ctrlKey || e.metaKey) {
      // Toggle selection
      setSelectedFileIds(prev => {
        const next = new Set(prev);
        if (next.has(file.id)) next.delete(file.id); else next.add(file.id);
        return next;
      });
      lastClickedIdxRef.current = idx;
    } else if (e.shiftKey && lastClickedIdxRef.current !== null) {
      // Range selection
      const from = Math.min(lastClickedIdxRef.current, idx);
      const to   = Math.max(lastClickedIdxRef.current, idx);
      setSelectedFileIds(prev => {
        const next = new Set(prev);
        filteredFiles.slice(from, to + 1).forEach(f => next.add(f.id));
        return next;
      });
    } else {
      // Plain click without modifier: clear selection, record position
      setSelectedFileIds(new Set());
      lastClickedIdxRef.current = idx;
    }
  };

  const addSelectedFiles = () => {
    const toAdd = filteredFiles.filter(f => selectedFileIds.has(f.id));
    if (onFilesAdd) {
      onFilesAdd(toAdd);
    } else {
      toAdd.forEach(f => onFileDoubleClick(f));
    }
    setSelectedFileIds(new Set());
    lastClickedIdxRef.current = null;
  };

  const filteredFolders = folders.filter(f =>
    !search || f.name.toLowerCase().includes(search.toLowerCase())
  );
  const filteredFiles = files.filter(f =>
    !search || f.original_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', ...glassSx, borderRadius: '14px' }}>

      {/* Controls bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '16px', py: '7px', borderBottom: '1px solid var(--divider)', flexShrink: 0 }}>

        {/* Navigation: back button + current location */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
          {folderStack.length > 1 ? (
            <>
              <Box component="button"
                onClick={() => navToStackIndex(folderStack.length - 2)}
                sx={{ width: 26, height: 26, borderRadius: '7px', flexShrink: 0, ...glassSx, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-secondary)', '&:hover': { background: 'var(--glass-hover)', color: 'var(--text-primary)' } }}>
                <IcoBack />
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {folderStack[folderStack.length - 1].name}
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              {t('media.allFiles')}
            </Typography>
          )}
        </Box>

        {/* Search */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', ...glassSx, borderRadius: '9px', px: '10px', py: '5px', width: 180, flexShrink: 0, '&:focus-within': { borderColor: 'var(--accent)' } }}>
          <Box sx={{ color: 'var(--text-disabled)', display: 'flex', flexShrink: 0 }}><IcoSearch /></Box>
          <Box component="input" value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            placeholder={t('media.searchPlaceholder')}
            sx={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 13, flex: 1, minWidth: 0, fontFamily: 'inherit', '&::placeholder': { color: 'var(--text-disabled)' } }} />
        </Box>

        {/* Selection action */}
        {selectedFileIds.size > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#818cf8' }}>
              {selectedFileIds.size} selected
            </Typography>
            <Box component="button" onClick={addSelectedFiles}
              sx={{ px: '10px', py: '4px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', background: 'rgba(99,102,241,.85)', color: '#fff', transition: 'opacity .12s', '&:hover': { opacity: 0.88 } }}>
              Add {selectedFileIds.size}
            </Box>
            <Box component="button" onClick={() => { setSelectedFileIds(new Set()); lastClickedIdxRef.current = null; }}
              sx={{ width: 22, height: 22, borderRadius: '6px', border: '1px solid var(--glass-border)', background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-disabled)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', '&:hover': { color: '#f87171' } }}>
              ✕
            </Box>
          </Box>
        )}

        {/* View mode toggle */}
        <Box sx={{ display: 'flex', ...glassSx, borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
          {(['list', 'grid'] as const).map(mode => (
            <Box key={mode} component="button"
              onClick={() => { setViewMode(mode); localStorage.setItem('pv_playlist_view', mode); }}
              sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', transition: 'all .12s', background: viewMode === mode ? 'rgba(99,102,241,.22)' : 'transparent', color: viewMode === mode ? 'var(--accent-light)' : 'var(--text-disabled)', '&:hover': viewMode === mode ? {} : { background: 'rgba(255,255,255,.06)', color: 'var(--text-secondary)' } }}>
              {mode === 'grid' ? <IcoGrid /> : <IcoList />}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Selection hint */}
      {selectedFileIds.size === 0 && (
        <Box sx={{ px: '16px', py: '4px', borderBottom: '1px solid var(--divider)', bgcolor: 'rgba(99,102,241,.04)', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)' }}>
            Double-click to add · Ctrl/⌘+click to select · Shift+click for range
          </Typography>
        </Box>
      )}

      {/* Content area */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: viewMode === 'list' ? 0 : '16px', pt: viewMode === 'list' ? 0 : '12px', pb: viewMode === 'list' ? 0 : '16px', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,.08)', borderRadius: 4 } }}>
        {loading ? (
          <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', py: '20px', textAlign: 'center' }}>{t('common.loading')}</Typography>
        ) : (
          <>
            {/* ── GRID VIEW ─────────────────────────────────────────────── */}
            {viewMode === 'grid' && (
              <>
                {filteredFolders.length > 0 && (
                  <>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--text-disabled)', mb: '8px' }}>{t('media.folders')}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(128px, 1fr))', gap: '8px', mb: '16px' }}>
                      {filteredFolders.map(folder => (
                        <MediaFolderCard key={folder.id} folder={folder} onClick={() => openFolder(folder)} />
                      ))}
                    </Box>
                  </>
                )}
                {filteredFiles.length > 0 && (
                  <>
                    {filteredFolders.length > 0 && (
                      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--text-disabled)', mb: '8px' }}>Files</Typography>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(128px, 1fr))', gap: '8px' }}>
                      {filteredFiles.map((file, fi) => {
                        const isSelected = selectedFileIds.has(file.id);
                        const dragFiles = isSelected && selectedFileIds.size > 1
                          ? filteredFiles.filter(f => selectedFileIds.has(f.id))
                          : [file];
                        return (
                          <Box key={file.id} sx={{ position: 'relative', outline: isSelected ? '2px solid #818cf8' : '2px solid transparent', borderRadius: '8px', transition: 'outline-color .1s', userSelect: 'none' }}
                            onClick={(e: React.MouseEvent) => handleFileClick(e, file, fi)}>
                            <MediaFileCard
                              file={file}
                              draggable={!!onFileDragStart}
                              onDragStart={onFileDragStart ? (e: React.DragEvent) => onFileDragStart(e, dragFiles) : undefined}
                              onDragEnd={onDragEnd}
                              onDoubleClick={() => onFileDoubleClick(file)}
                              showTypeBadge
                              showDragHint={showDragHint && !isSelected}
                              dblClickHint={t('playlists.dblClickToAdd')}
                              dragHint={t('playlists.dragToInsert')}
                            />
                            {isSelected && (
                              <Box sx={{ position: 'absolute', top: '6px', right: '6px', width: 18, height: 18, borderRadius: '50%', bgcolor: '#818cf8', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,.4)', zIndex: 10 }}>
                                <svg width={10} height={10} viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}
              </>
            )}

            {/* ── LIST VIEW ─────────────────────────────────────────────── */}
            {viewMode === 'list' && (
              <>
                {(filteredFolders.length > 0 || filteredFiles.length > 0) && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px', px: '16px', py: '5px', borderBottom: '1px solid var(--divider)', background: 'rgba(0,0,0,.05)' }}>
                    {[t('playlists.colName'), 'Size', t('playlists.colDuration')].map((h, i) => (
                      <Typography key={i} sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-disabled)' }}>{h}</Typography>
                    ))}
                  </Box>
                )}

                {/* Back row */}
                {folderStack.length > 1 && (
                  <Box onClick={() => navToStackIndex(folderStack.length - 2)}
                    sx={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px', px: '16px', py: '6px', alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid var(--divider)', transition: 'background .1s', '&:hover': { background: 'rgba(255,255,255,.04)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                      <Box sx={{ width: 48, height: 28, flexShrink: 0, borderRadius: '5px', background: 'rgba(255,255,255,.04)', border: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                        <IcoBack />
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {folderStack[folderStack.length - 2].name || t('media.allFiles')}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}></Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}></Typography>
                  </Box>
                )}

                {/* Folder rows */}
                {filteredFolders.map(folder => (
                  <Box key={folder.id} onClick={() => openFolder(folder)}
                    sx={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px', px: '16px', py: '6px', alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid var(--divider)', transition: 'background .1s', '&:hover': { background: 'rgba(255,255,255,.04)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                      <Box sx={{ width: 48, height: 28, flexShrink: 0, borderRadius: '5px', background: 'linear-gradient(135deg,rgba(99,102,241,.18),rgba(99,102,241,.06))', border: '1px solid rgba(99,102,241,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(99,102,241,.7)' }}>
                        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{folder.name}</Typography>
                      {folder.file_count !== undefined && (
                        <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', flexShrink: 0 }}>{folder.file_count} items</Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>—</Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>—</Typography>
                  </Box>
                ))}

                {/* File rows */}
                {filteredFiles.map((file, fi) => {
                  const vid = isVideo(file.mime_type);
                  const isSelected = selectedFileIds.has(file.id);
                  const dragFiles = isSelected && selectedFileIds.size > 1
                    ? filteredFiles.filter(f => selectedFileIds.has(f.id))
                    : [file];
                  return (
                    <Box key={file.id}
                      draggable={!!onFileDragStart}
                      onDragStart={onFileDragStart ? (e: React.DragEvent) => onFileDragStart(e, dragFiles) : undefined}
                      onDragEnd={onDragEnd}
                      onClick={(e: React.MouseEvent) => handleFileClick(e, file, fi)}
                      onDoubleClick={() => onFileDoubleClick(file)}
                      sx={{
                        display: 'grid', gridTemplateColumns: '1fr 80px 90px', px: '16px', py: '6px', alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid var(--divider)', transition: 'background .1s',
                        borderLeft: isSelected ? '3px solid #818cf8' : '3px solid transparent',
                        bgcolor: isSelected ? 'rgba(99,102,241,.12)' : 'transparent',
                        '&:hover': { bgcolor: isSelected ? 'rgba(99,102,241,.18)' : showDragHint ? 'rgba(99,102,241,.06)' : 'rgba(255,255,255,.04)' },
                        userSelect: 'none',
                      }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                        <Box sx={{ width: 48, height: 28, flexShrink: 0, borderRadius: '5px', overflow: 'hidden', position: 'relative', background: vid ? 'linear-gradient(135deg,#071a30,#0f3d6e)' : 'linear-gradient(135deg,#064e3b,#0e7c5a)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <ThumbImg fileId={file.id} />
                          {vid && (
                            <Box sx={{ color: 'rgba(255,255,255,.5)', display: 'flex', position: 'relative', zIndex: 1 }}>
                              <svg width={10} height={10} viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                            </Box>
                          )}
                          {/* Selection checkmark overlay */}
                          {isSelected && (
                            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(99,102,241,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                              <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: '#818cf8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width={9} height={9} viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </Box>
                            </Box>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: isSelected ? 'var(--accent-light)' : 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.original_name}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>
                        {file.size_bytes > 0 ? `${(file.size_bytes / 1024 / 1024).toFixed(1)} MB` : '—'}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>
                        {file.duration_ms ? fmtDuration(file.duration_ms) : '—'}
                      </Typography>
                    </Box>
                  );
                })}
              </>
            )}

            {/* Empty state */}
            {filteredFolders.length === 0 && filteredFiles.length === 0 && !loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: '40px' }}>
                <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>
                  {search ? t('common.noData') : t('media.noFiles')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MediaBrowserPanel;
